import {
  useEffect,
  useRef,
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import GlobeGL, { type GlobeMethods } from "react-globe.gl";
import * as THREE from "three";
import type { CountryFeature, HoverPosition } from "../types";
import { sameCountry } from "../utils/country";
import {
  countriesToPaths,
  type CountryPath,
  type PathPoint,
} from "../utils/paths";
import {
  makeCore,
  makeGlow,
  makeGraticule,
  makeStarfield,
} from "../three/layers";
import {
  RIPPLE_COUNT,
  createRippleUniforms,
  generateCountryDots,
  makeCountryPoints,
  makeIlluminationHalos,
  type RippleUniforms,
} from "../three/dots";

/**
 * ⚠ VENDORED, NOT HOTLINKED. This was pointed at raw.githubusercontent.com, which
 * put a third party on the critical path of the page's only content: GitHub rate-
 * limits raw fetches by IP, serves it with no CDN guarantee, and the whole scene
 * renders empty when it fails — with no error, because the catch path just leaves
 * the country list empty. 477KB of Natural Earth 110m admin-0 (177 features) is
 * a rounding error next to the three.js bundle, and it makes the page work
 * offline and behind a CSP that forbids third-party connect-src.
 */
const GEOJSON_URL = "/data/countries.geojson";

export interface GlobeHandle {
  flyTo: (lat: number, lng: number, altitude?: number) => void;
  setAutoRotate: (on: boolean) => void;
  playEntrance: () => void;
}

interface Props {
  width: number;
  height: number;
  selected: CountryFeature | null;
  onHover: (c: CountryFeature | null, pos: HoverPosition | null) => void;
  onSelect: (c: CountryFeature | null) => void;
  onLoaded?: (count: number) => void;
  onReady?: () => void;
}

const Globe = forwardRef<GlobeHandle, Props>(function Globe(
  { width, height, selected, onHover, onSelect, onLoaded, onReady },
  ref
) {
  const globeRef = useRef<GlobeMethods | undefined>(undefined);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const sharedTimeRef = useRef<{ value: number } | null>(null);
  const rippleUniformsRef = useRef<RippleUniforms | null>(null);
  const rippleStateRef = useRef<{ start: number }[]>([]);
  // Live flags read by the WebGL tick to skip cube-camera work during lifts.
  const liftActiveRef = useRef({ hover: false, select: false });
  const [countries, setCountries] = useState<CountryFeature[]>([]);
  const [hovered, setHovered] = useState<CountryFeature | null>(null);
  // debounce handle for transitions to null — three-globe fires null between
  // the ring meshes of MultiPolygon countries (USA, Russia, Canada…), which
  // would otherwise keep resetting the lift animation. Swallow briefly.
  const nullHoverTimerRef = useRef<number | null>(null);

  // Base outlines — always on the globe at the dot-layer altitude.
  // These never move so the angular shape of every country is permanent.
  const basePaths = useMemo(
    () => countriesToPaths(countries, () => 0.012),
    [countries]
  );

  // O(1) country → rings lookup. Previously we scanned all basePaths every
  // animation frame (up to ~2000 paths × 25 frames = 50k comparisons); now
  // the filtered ring set is found in constant time per lift target.
  const pathsByCountry = useMemo(() => {
    const map = new Map<CountryFeature, CountryPath[]>();
    for (const p of basePaths) {
      const list = map.get(p.country);
      if (list) list.push(p);
      else map.set(p.country, [p]);
    }
    return map;
  }, [basePaths]);

  // Two independent lift progress values — one for hover ghost, one for
  // select ghost. This matters because moving around inside the *selected*
  // country was previously resetting the single shared progress and making
  // the select ghost drop+re-rise on every hover change.
  const [hoverLift, setHoverLift] = useState(0);
  const [selectLift, setSelectLift] = useState(0);

  const animateLift = (
    setter: (v: number) => void,
    onActive: (active: boolean) => void,
    duration = 700
  ): (() => void) => {
    const start = performance.now();
    const minDelta = 1 / 30; // ~30fps emission rate — smooth but cheaper
    let lastEmitted = 0;
    let raf = 0;
    onActive(true);
    const tick = () => {
      const elapsed = performance.now() - start;
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      if (eased - lastEmitted >= minDelta || p === 1) {
        lastEmitted = eased;
        setter(eased);
      }
      if (p < 1) raf = requestAnimationFrame(tick);
      else onActive(false);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      onActive(false);
    };
  };

  // Hover lift — restarts ONLY when hovered changes, and skipped entirely
  // when the hovered country is the already-selected one.
  useEffect(() => {
    if (!hovered || sameCountry(hovered, selected)) {
      setHoverLift(0);
      liftActiveRef.current.hover = false;
      return;
    }
    setHoverLift(0);
    return animateLift(
      setHoverLift,
      (on) => (liftActiveRef.current.hover = on)
    );
  }, [hovered, selected]);

  // Select lift — restarts ONLY when selected changes; unaffected by hover.
  useEffect(() => {
    if (!selected) {
      setSelectLift(0);
      liftActiveRef.current.select = false;
      return;
    }
    setSelectLift(0);
    return animateLift(
      setSelectLift,
      (on) => (liftActiveRef.current.select = on)
    );
  }, [selected]);

  // Ghost copies for hover and select. Each uses its own lift progress, so
  // the select ghost stays perfectly steady while the hover ghost animates.
  const countryPaths = useMemo<CountryPath[]>(() => {
    if (!hovered && !selected) return basePaths;
    const BASE_ALT = 0.012;
    const extras: CountryPath[] = [];

    if (hovered && !sameCountry(hovered, selected)) {
      const rings = pathsByCountry.get(hovered);
      if (rings) {
        const alt = BASE_ALT + hoverLift * (0.08 - BASE_ALT);
        for (const base of rings) {
          extras.push({
            country: base.country,
            key: `${base.key}:h`,
            lifted: true,
            role: "hover",
            points: base.points.map(
              ([lat, lng]) => [lat, lng, alt] as PathPoint
            ),
          });
        }
      }
    }

    if (selected) {
      const rings = pathsByCountry.get(selected);
      if (rings) {
        const alt = BASE_ALT + selectLift * (0.12 - BASE_ALT);
        for (const base of rings) {
          extras.push({
            country: base.country,
            key: `${base.key}:s`,
            lifted: true,
            role: "select",
            points: base.points.map(
              ([lat, lng]) => [lat, lng, alt] as PathPoint
            ),
          });
        }
      }
    }

    if (extras.length === 0) return basePaths;
    return basePaths.concat(extras);
  }, [basePaths, pathsByCountry, hovered, selected, hoverLift, selectLift]);

  useEffect(() => {
    let cancelled = false;
    fetch(GEOJSON_URL)
      .then((r) => r.json())
      .then((json: { features: CountryFeature[] }) => {
        if (cancelled) return;
        const features = json.features.filter(
          (f) =>
            (f.properties.ISO_A2 ?? f.properties.iso_a2) !== "AQ" &&
            (f.properties.ADMIN ?? f.properties.admin) !== "Antarctica"
        );
        setCountries(features);
        onLoaded?.(features.length);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [onLoaded]);

  const globeMaterial = useMemo(() => {
    return new THREE.MeshBasicMaterial({
      color: new THREE.Color("#000000"),
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
  }, []);

  useEffect(() => {
    const g = globeRef.current;
    if (!g) return;

    const scene = g.scene();
    scene.children
      .filter((o: THREE.Object3D) => (o as THREE.Light).isLight)
      .forEach((l: THREE.Object3D) => scene.remove(l));

    // neutral white lighting — all warm accents stripped
    const ambient = new THREE.AmbientLight(0xffffff, 0.25);
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.45);
    keyLight.position.set(-3, 2, 4);
    scene.add(ambient, keyLight);

    // Cube reflection probe — 72px per face (6 faces = ~32k fragments
    // vs the original 128² × 6 = 98k), no mipmaps, linear filter. The
    // result is soft regardless because it's rendered on a near-mirror
    // sphere with subtle roughness.
    const cubeRT = new THREE.WebGLCubeRenderTarget(72, {
      format: THREE.RGBAFormat,
      generateMipmaps: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
    });
    const cubeCam = new THREE.CubeCamera(0.5, 2000, cubeRT);
    cubeCam.position.set(0, 0, 0);
    scene.add(cubeCam);

    const core = makeCore(cubeRT.texture);
    const graticule = makeGraticule();
    const glow = makeGlow();
    const stars = makeStarfield(1600);
    scene.add(core, graticule, glow, stars);

    const controls = g.controls();
    controls.autoRotate = true;
    controls.autoRotateSpeed = 0.22;
    controls.enableDamping = true;
    controls.dampingFactor = 0.14; // snappier settle → smoother perceived zoom
    controls.enableZoom = true;
    controls.zoomSpeed = 0.8;
    controls.rotateSpeed = 0.7;
    controls.minDistance = 180;
    controls.maxDistance = 800;

    // Seed POV at the horizon start position so there's no jump when the
    // entrance animation fires.
    g.pointOfView({ lat: 38, lng: -95, altitude: 0.12 }, 0);
    onReadyRef.current?.();

    const start = performance.now();
    let raf = 0;
    const gratMat = graticule.material as THREE.ShaderMaterial;
    const glowMat = glow.material as THREE.ShaderMaterial;
    const starsMat = stars.material as THREE.ShaderMaterial;
    const renderer = g.renderer();

    const sharedTime = { value: 0 };
    sharedTimeRef.current = sharedTime;

    // ripple state: N wavefronts, staggered starts, regenerated on cycle end
    const rippleUniforms = createRippleUniforms();
    rippleUniformsRef.current = rippleUniforms;
    const rippleDuration = 30; // seconds per wave — very slow, ambient
    const randomUnit = () => {
      const u = Math.random();
      const v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      return new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta),
        Math.cos(phi),
        Math.sin(phi) * Math.sin(theta)
      );
    };
    const rippleState = Array.from({ length: RIPPLE_COUNT }, (_, i) => ({
      start: -i * (rippleDuration / RIPPLE_COUNT),
    }));
    rippleStateRef.current = rippleState;
    // seed initial centers
    rippleUniforms.uRippleCenters.value = rippleState.map(() => randomUnit());

    // Detect active camera motion (user zoom/pan/rotate) so we can skip
    // expensive probe passes that would stutter the interaction.
    const camera = g.camera();
    let lastCamDist = camera.position.length();
    let lastCamX = camera.position.x;
    let lastCamY = camera.position.y;
    let motionCooldown = 0;

    let frame = 0;
    let lastT = 0;
    const tick = () => {
      const t = (performance.now() - start) / 1000;
      const dt = t - lastT;
      lastT = t;
      frame++;

      // Shared uniforms — time / ripple — only needed every 2 frames since
      // all animations driven by uTime run on long timescales (breath 3s+,
      // ripple 30s). Visually indistinguishable from per-frame updates.
      if (frame % 2 === 0) {
        gratMat.uniforms.uTime.value = t;
        glowMat.uniforms.uTime.value = t;
        starsMat.uniforms.uTime.value = t;
        sharedTime.value = t;

        // Ripple state — wave travels <0.02 rad per frame; updating every
        // other frame is imperceptible.
        for (let i = 0; i < RIPPLE_COUNT; i++) {
          const age = t - rippleState[i].start;
          if (age >= rippleDuration) {
            rippleState[i].start = t;
            rippleUniforms.uRippleCenters.value[i].copy(randomUnit());
            rippleUniforms.uRippleProgress.value[i] = 0;
          } else {
            rippleUniforms.uRippleProgress.value[i] = age / rippleDuration;
          }
        }
      }

      // Camera motion detection
      const dist = camera.position.length();
      const dx = camera.position.x - lastCamX;
      const dy = camera.position.y - lastCamY;
      const moved = Math.abs(dist - lastCamDist) > 0.05 || dx * dx + dy * dy > 0.05;
      if (moved) {
        motionCooldown = 12; // keep probe paused ~200ms after motion stops
      } else if (motionCooldown > 0) {
        motionCooldown--;
      }
      lastCamDist = dist;
      lastCamX = camera.position.x;
      lastCamY = camera.position.y;

      // Reflection probe — every 4th frame, paused during lifts AND any
      // active camera motion. Each probe = 6 scene renders; skipping during
      // interaction keeps zoom/rotate silky.
      const liftActive =
        liftActiveRef.current.hover || liftActiveRef.current.select;
      if (frame % 4 === 0 && !liftActive && motionCooldown === 0) {
        core.visible = false;
        glow.visible = false;
        cubeCam.update(renderer, scene);
        core.visible = true;
        glow.visible = true;
      }

      // Graticule rotation — delta-time-based so it stays smooth under
      // variable frame rate.
      graticule.rotation.y += dt * 0.03;
      graticule.rotation.x = Math.sin(t * 0.08) * 0.03;

      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      scene.remove(core, graticule, glow, stars, cubeCam);
      scene.remove(ambient, keyLight);
      (core.geometry as THREE.BufferGeometry).dispose();
      (graticule.geometry as THREE.BufferGeometry).dispose();
      (glow.geometry as THREE.BufferGeometry).dispose();
      (stars.geometry as THREE.BufferGeometry).dispose();
      (core.material as THREE.Material).dispose();
      gratMat.dispose();
      glowMat.dispose();
      starsMat.dispose();
      cubeRT.dispose();
    };
    /* Deliberately mount-once. This effect BUILDS the scene — core, graticule,
       glow, starfield, env probe — and its cleanup disposes every material and
       render target it made. Adding the deps the rule wants would tear the
       whole scene down and rebuild it on any of them changing, which is both a
       visible flash and a GPU allocation churn. The values it closes over are
       refs and constants, so there is nothing stale to capture. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // scatter fiber-optic points inside each country polygon (no hex grid)
  // + illumination halos that cast the dots' glow onto the core sphere
  useEffect(() => {
    if (!countries.length) return;
    const g = globeRef.current;
    if (!g) return;
    const scene = g.scene();

    const timeRef = sharedTimeRef.current ?? { value: 0 };
    const rippleRef = rippleUniformsRef.current ?? createRippleUniforms();
    const data = generateCountryDots(countries, 1.6);
    const points = makeCountryPoints(data, timeRef, rippleRef);
    const illum = makeIlluminationHalos(data, timeRef, rippleRef);
    scene.add(points, illum);

    return () => {
      scene.remove(points, illum);
      (points.geometry as THREE.BufferGeometry).dispose();
      (points.material as THREE.Material).dispose();
      (illum.geometry as THREE.BufferGeometry).dispose();
      (illum.material as THREE.Material).dispose();
    };
  }, [countries]);

  useImperativeHandle(
    ref,
    () => ({
      flyTo(lat: number, lng: number, altitude = 1.6) {
        globeRef.current?.pointOfView({ lat, lng, altitude }, 1400);
      },
      setAutoRotate(on: boolean) {
        const c = globeRef.current?.controls();
        if (c) c.autoRotate = on;
      },
      playEntrance() {
        const g = globeRef.current;
        if (!g) return;
        // Start low over North America — camera just above the dot field
        // so the sphere's horizon curves into frame, pull back slowly to
        // the settled world view. Classic satellite-rising-to-orbit shot.
        g.pointOfView({ lat: 38, lng: -95, altitude: 0.12 }, 0);
        setTimeout(() => {
          g.pointOfView({ lat: 22, lng: 18, altitude: 2.4 }, 5500);
        }, 120);
      },
    }),
    []
  );

  // polygon overlay stays fully invisible; used only for hit-testing
  const capColor = () => "rgba(0,0,0,0)";
  const sideColor = () => "rgba(0,0,0,0)";
  const strokeColor = () => "rgba(0,0,0,0)";
  const polyAlt = () => 0.001;

  // Baseline paths stay thin + steady. Each lifted ghost reads from its own
  // lift progress (hover or select) so their animations are fully decoupled.
  const pathColor = (p: object) => {
    const cp = p as CountryPath;
    if (!cp.lifted) return "rgba(255,255,255,0.55)";
    const lift = cp.role === "select" ? selectLift : hoverLift;
    const targetA = cp.role === "select" ? 1.0 : 0.95;
    const a = 0.55 + lift * (targetA - 0.55);
    return `rgba(255,255,255,${a})`;
  };
  const pathStroke = (p: object) => {
    const cp = p as CountryPath;
    if (!cp.lifted) return 0.55;
    const lift = cp.role === "select" ? selectLift : hoverLift;
    const target = cp.role === "select" ? 3.5 : 2.5;
    return 0.55 + lift * (target - 0.55);
  };

  return (
    <GlobeGL
      ref={globeRef}
      width={width}
      height={height}
      backgroundColor="rgba(0,0,0,0)"
      globeMaterial={globeMaterial}
      showGlobe
      showGraticules={false}
      showAtmosphere
      atmosphereColor="#ffffff"
      atmosphereAltitude={0.18}
      /* `as unknown as object[]` three times below, and it is react-globe.gl's
         typing rather than ours: its data props are declared `object[]` and its
         accessors `(d: object) => …`, so a typed feature array cannot be passed
         without widening. The casts are at the boundary only — everything
         upstream stays `CountryFeature` / `CountryPath`. */
      pathsData={countryPaths as unknown as object[]}
      pathPoints={(p: object) => (p as CountryPath).points as unknown as object[]}
      pathPointLat={(pt: unknown) => (pt as PathPoint)[0]}
      pathPointLng={(pt: unknown) => (pt as PathPoint)[1]}
      pathPointAlt={(pt: unknown) => (pt as PathPoint)[2]}
      pathColor={pathColor}
      pathStroke={pathStroke}
      pathResolution={2}
      pathTransitionDuration={0}
      polygonsData={countries as unknown as object[]}
      polygonCapColor={capColor}
      polygonSideColor={sideColor}
      polygonStrokeColor={strokeColor}
      polygonAltitude={polyAlt}
      polygonsTransitionDuration={350}
      polygonLabel={() => ""}
      onPolygonHover={(p) => {
        const next = (p as CountryFeature | null) ?? null;
        // any fresh event cancels a pending null-out
        if (nullHoverTimerRef.current !== null) {
          window.clearTimeout(nullHoverTimerRef.current);
          nullHoverTimerRef.current = null;
        }
        if (next) {
          // commit immediately when over a country (React bails out if same ref)
          setHovered(next);
          const controls = globeRef.current?.controls();
          if (controls) controls.autoRotate = false;
          onHover(next, null);
        } else {
          // Defer un-hover so brief gaps between sub-polygons of a single
          // country don't restart the lift animation.
          nullHoverTimerRef.current = window.setTimeout(() => {
            nullHoverTimerRef.current = null;
            setHovered(null);
            const controls = globeRef.current?.controls();
            if (controls) controls.autoRotate = !selected;
            onHover(null, null);
          }, 180);
        }
      }}
      onPolygonClick={(p, event) => {
        const f = p as CountryFeature;
        const mouseEvent = event as MouseEvent;
        onSelect(f);
        onHover(f, { x: mouseEvent.clientX, y: mouseEvent.clientY });
      }}
      onGlobeClick={() => onSelect(null)}
    />
  );
});

export default Globe;
